import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { Select } from '../ui/Select.js';
import { ContactSelector, type ContactSelectorValue } from '../ui/ContactSelector.js';
import { useToast } from '../ui/Toast.js';
import { useSettingsTabLabel } from '../ui/SettingsLink.js';
import { CreateContactForm } from '../contacts/CreateContactForm.js';
import { useContactProjects } from '../../hooks/useProjects.js';
import { useCreateEstimate, useAccounts } from '../../hooks/useBilling.js';

// Mirrors CreateProjectModal (components/projects/CreateProjectModal.tsx)
// exactly for the contact step — same ContactSelector + inline
// CreateContactForm reveal, same Modal shell. Deliberately simpler:
// estimates only need a contact (this always creates a draft ESTIMATE,
// never an invoice — invoices only come from accepting one). Account
// stays defaulted-to-active (no modal account picker — approved, see
// BillingPage.tsx's original flag). Project link is optional, biased to
// the selected contact's own projects via the existing useContactProjects
// hook (same one ContactProjectsTab uses) — reused, not rebuilt.

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
  /** Cliente precargado — viene del + de la tarjeta Facturación de un contacto. */
  preselectedContact?: { id: number; name: string } | null;
}

export function CreateEstimateModal({ open, onClose, onCreated, preselectedContact }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  // El nombre de la pestaña sale del i18n, nunca escrito en la cadena: eso es
  // lo que mandó a un cliente a buscar una pestaña "Billing" que no existe.
  const tabFacturacion = useSettingsTabLabel('data');
  const { data: accountsData } = useAccounts();
  const createEstimate = useCreateEstimate();
  const [contact, setContact] = useState<ContactSelectorValue | null>(preselectedContact ?? null);
  const [showNewContact, setShowNewContact] = useState(false);
  const [projectId, setProjectId] = useState<number | null>(null);
  const { data: projectsData } = useContactProjects(contact?.id);
  const contactProjects = projectsData?.items ?? [];

  useEffect(() => {
    if (open) {
      setContact(null);
      setShowNewContact(false);
      setProjectId(null);
    }
  }, [open]);

  // Contact changed (or cleared) — the previously-picked project no longer
  // applies, and a brand-new contact has no projects to show yet anyway.
  useEffect(() => {
    setProjectId(null);
  }, [contact?.id]);

  const isSubmitting = createEstimate.isPending;
  const canSubmit = contact !== null && !isSubmitting;

  async function handleSubmit() {
    if (!contact) return;
    const accounts = accountsData?.accounts ?? [];
    const account = accounts.find(a => a.isActive) ?? accounts[0];
    if (!account) {
      toast.show({ message: t('billing.estimates.noAccounts', { tab: tabFacturacion }), tone: 'error' });
      return;
    }
    try {
      const result = await createEstimate.mutateAsync({
        accountId: account.id,
        contactId: contact.id,
        projectId,
        lineItems: [],
      });
      onClose();
      onCreated(result.id);
    } catch {
      toast.show({ message: t('common.error'), tone: 'error' });
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('billing.facturacion.createTitle')}
      size="sm"
      preventClose={isSubmitting}
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose} disabled={isSubmitting}>
            {t('projects.create.cancel')}
          </Button>
          <Button
            tone="cyan"
            onClick={() => { void handleSubmit(); }}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            {t('billing.facturacion.createSubmit')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <ContactSelector
            label={t('projects.create.contact')}
            value={contact}
            onChange={setContact}
            placeholder={t('projects.create.contactPlaceholder')}
            fullWidth
          />
          {!showNewContact && (
            <Button variant="link" tone="cyan" size="sm" type="button" onClick={() => setShowNewContact(true)}>
              {t('projects.create.newContact')}
            </Button>
          )}

          {showNewContact && (
            <div style={{
              marginTop: '8px', padding: '12px',
              background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
              borderRadius: 'var(--evo-r)',
            }}>
              <CreateContactForm
                onSuccess={c => { setContact(c); setShowNewContact(false); }}
                onCancel={() => setShowNewContact(false)}
              />
            </div>
          )}
        </div>

        {contact && (
          <Select<number>
            label={t('billing.facturacion.project')}
            value={projectId}
            onChange={setProjectId}
            options={contactProjects.map(p => ({ value: p.id, label: p.name }))}
            placeholder={contactProjects.length === 0 ? t('billing.facturacion.projectNone') : t('billing.facturacion.projectPlaceholder')}
            clearable
            fullWidth
          />
        )}
      </div>
    </Modal>
  );
}
